import { createContext, useContext } from "react"

import type { Session } from "@supabase/supabase-js"

/**
 * Three roles.
 *
 * `admin` and `operator` are HIVED's own and identical throughout the app, except that `admin`
 * may manage the access list. `partner` is another company running some of our routes: it sees
 * only the routes carrying its operator, read-only apart from putting a driver on one. Staff
 * sign in with Google; a partner signs up with an address an admin has added, and a password of
 * their own.
 *
 * The role comes from `app_metadata.collections_role`, injected on every token issue by the
 * custom_access_token_hook from the collections_access table, with a partner's operator beside
 * it as `collections_operator`. app_metadata is not writable by the user, so neither can be
 * self-granted, and removing someone's row revokes them at their next token refresh.
 *
 * A signed-in account with no row on the allowlist has a null role — authenticated, but not
 * authorised. ProtectedRoute turns that into a refusal.
 */
export type Role = "admin" | "operator" | "partner"

/**
 * What a role may see and do, named for the thing rather than for the role.
 *
 * Screens ask `can.editRoutes`, not "is this a partner", so the answer to "what does a partner
 * get" is `capabilitiesFor` below and nowhere else. None of this is the security boundary: the
 * edge functions decide what a login is sent and what it may write, from the access list. These
 * flags only keep the app from offering what would be refused.
 */
export interface Capabilities {
  /** Every board. Without it: Monitoring's Map and Timeline, and Planning's Timeline and List. */
  allBoards: boolean
  /** Create, change and cancel routes and stops. Without it the boards are read-only. */
  editRoutes: boolean
  /** Costs, revenue and load factor, and the config and site data they are worked out from. */
  costs: boolean
  /** Booked shifts set against the plan. */
  shiftCover: boolean
  /** Comments and change history. */
  activity: boolean
  /** Put a vehicle or trailer on a route. Putting a driver on one is open to every role. */
  assignVehicles: boolean
  /**
   * Narrow the plan by what kind of route it is: Kind on the list, and the timeline's haulage,
   * empty-leg and stand-by filters. Search, dates and status stay with every role.
   */
  planFilters: boolean
}

const NOTHING: Capabilities = {
  allBoards: false,
  editRoutes: false,
  costs: false,
  shiftCover: false,
  activity: false,
  assignVehicles: false,
  planFilters: false,
}

const EVERYTHING: Capabilities = {
  allBoards: true,
  editRoutes: true,
  costs: true,
  shiftCover: true,
  activity: true,
  assignVehicles: true,
  planFilters: true,
}

/** A partner's capabilities are exactly "none of the optional ones": what remains is the default. */
export function capabilitiesFor(role: Role | null): Capabilities {
  return role === "admin" || role === "operator" ? EVERYTHING : NOTHING
}

/** The Google Workspace everyone at HIVED signs in through. */
export const STAFF_EMAIL_DOMAIN = "hived.space"

/**
 * Whether an address is one of HIVED's own.
 *
 * Those sign in with Google and nothing else. The email-and-password forms are for partner
 * companies, and turn a HIVED address away rather than start a second way into an account whose
 * security is Google's to look after.
 */
export function isStaffEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith(`@${STAFF_EMAIL_DOMAIN}`)
}

/**
 * The shortest password the sign-up form and the change-password form accept. Mirrors the floor
 * collections-partner-signup enforces, so the form can say so before the server does.
 */
export const MIN_PASSWORD_LENGTH = 12

export interface AuthUser {
  id: string
  email: string | null
  /** Display name, used to attribute comments and timeline edits. */
  full_name: string | null
  /** null when the account is not on the allowlist. */
  role: Role | null
  /** The one operator a partner sees. null for admin and operator, who are not scoped. */
  operator: string | null
}

export interface AuthContextValue {
  user: AuthUser | null
  session: Session | null
  loading: boolean
  /** Signed in AND on the allowlist. */
  isAuthorised: boolean
  /** May manage the access list. */
  isAdmin: boolean
  /** Another company's login, confined to its own operator's routes. */
  isPartner: boolean
  can: Capabilities
  signInWithGoogle: () => Promise<void>
  /** Partner logins. Rejects with the reason when the email or password is wrong. */
  signInWithPassword: (email: string, password: string) => Promise<void>
  /**
   * Create a partner's account and sign them in. Only an address an admin has added can do this,
   * and only once; anything else rejects with a reason written for the person at the form.
   */
  signUp: (details: { email: string; password: string }) => Promise<void>
  /** Set a new password on the signed-in account. Rejects with the reason if refused. */
  changePassword: (password: string) => Promise<void>
  signOut: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider")
  return ctx
}
